import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiLock, FiMail } from 'react-icons/fi';
import { Input, Button, message, Spin } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { apiUrl } from '../api';
import logo from '../assets/logo.png';

const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [formErrors, setFormErrors] = useState({ identifier: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // HRMS appends "?token=<jwt>" to redirect_uri verbatim. If redirect_uri already
  // carried a query, the return URL can hold two "?"; fall back to a raw scan so
  // the JWT is recovered regardless of how HRMS joined it.
  const queryParams = new URLSearchParams(location.search);
  const hrmsToken =
    queryParams.get('token') ||
    location.search.match(/[?&]token=([^&]+)/)?.[1] ||
    null;
  const [ssoExchanging, setSsoExchanging] = useState(!!hrmsToken);

  const persistSession = (data) => {
    localStorage.setItem('token', data.token);
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    window.dispatchEvent(new Event('auth-change'));
  };

  // Exchange an HRMS SSO token for a VMS session on mount.
  useEffect(() => {
    if (!hrmsToken) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(apiUrl('/api/auth/hrms-sso'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tokenData: hrmsToken }),
        });
        const data = await response.json();
        if (cancelled) return;

        if (response.ok && data.success) {
          persistSession(data);
          message.success('Signed in with HRMS');
          onLoginSuccess?.();
          const dest = localStorage.getItem('ssoRedirect') || '/dashboard';
          localStorage.removeItem('ssoRedirect');
          navigate(dest);
        } else {
          message.error(data.message || 'HRMS sign-in failed. Please try again.');
          setSsoExchanging(false);
          // Drop the token param so the login form renders instead of a spinner.
          navigate('/login', { replace: true });
        }
      } catch {
        if (cancelled) return;
        message.error('HRMS sign-in failed. Please try again.');
        setSsoExchanging(false);
        navigate('/login', { replace: true });
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHrmsSso = () => {
    const hrmsUrl = import.meta.env.VITE_HRMS_SSO_URL;
    if (!hrmsUrl) {
      message.error('HRMS sign-in is not configured.');
      return;
    }
    // Query-free redirect_uri guarantees a clean "...?token=..." on return.
    const encodedRedirect = encodeURIComponent(`${window.location.origin}/login`);
    window.location.href = `${hrmsUrl}?redirect_uri=${encodedRedirect}`;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: '' });
    }
  };

  const handlePasswordChange = (e) => {
    setFormData({ ...formData, password: e.target.value });
    if (formErrors.password) {
      setFormErrors({ ...formErrors, password: '' });
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');

    const emailStr = (formData.identifier || '').trim();
    const passStr = (formData.password || '').trim();

    let hasError = false;
    let newErrors = { identifier: '', password: '' };

    if (!emailStr) {
      newErrors.identifier = 'Please enter your email!';
      hasError = true;
    }
    if (!passStr) {
      newErrors.password = 'Please enter your password!';
      hasError = true;
    }

    if (hasError) {
      setFormErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        persistSession(data);
        message.success('Logged in successfully!');
        onLoginSuccess?.();
        navigate('/dashboard');
      } else {
        setError(data.message || 'Login failed');
        message.error(data.message || 'Login failed');
      }
    } catch {
      setError('Connection error. Is the server running?');
      message.error('Connection error. Is the server running?');
    } finally {
      setLoading(false);
    }
  };

  if (ssoExchanging) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(180deg,#0b4d7b_0%,#067fc2_100%)]">
        <Spin size="large" tip="Signing in with HRMS…" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[linear-gradient(180deg,#0b4d7b_0%,#067fc2_100%)] flex items-center justify-center px-4 py-10 font-sans">
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute left-[-8%] top-[10%] h-64 w-64 rounded-full bg-white/10 blur-[90px]" />
        <div className="absolute right-[-10%] bottom-[8%] h-72 w-72 rounded-full bg-cyan-300/20 blur-[110px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-[520px]"
      >
        <div className="flex flex-col items-center gap-8">
          <div className="flex items-center justify-center gap-3">
            <img src={logo} alt="VMS Logo" className="h-20 w-auto rounded-[24px] object-contain bg-white/10 p-2 backdrop-blur-sm shadow-[0_16px_40px_rgba(0,0,0,0.18)]" />
            <div className="text-[54px] font-semibold leading-none tracking-[-0.05em] text-white">VMS</div>
          </div>

          <div className="wm-card w-full overflow-hidden rounded-[24px] border border-white/70 bg-white p-8 shadow-[0_22px_80px_rgba(2,24,49,0.24)] md:p-12">
            <div className="mb-8 text-center">
              <h1 className="text-[34px] font-semibold tracking-[-0.03em] text-slate-700">Sign In</h1>
              <p className="mt-2 text-[18px] text-slate-700">Welcome to VMS Portal !</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-700"
                >
                  <span className="mr-2">⚠</span> {error}
                </motion.div>
              )}

              <div className="space-y-2">
                <label className="block text-[18px] font-medium text-slate-700">
                  <span className="text-rose-500">*</span> Email
                </label>
                <Input
                  size="large"
                  prefix={<FiMail className="mr-2 text-sky-600" />}
                  name="identifier"
                  autoComplete="email"
                  value={formData.identifier}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  status={formErrors.identifier ? 'error' : ''}
                  className={`h-14 rounded-[10px] text-[16px] ${!formErrors.identifier ? 'border-slate-200 shadow-[0_0_0_1px_rgba(2,24,49,0.02)]' : ''}`}
                />
                {formErrors.identifier && <div className="text-[#ff4d4f] text-[14px] mt-1">{formErrors.identifier}</div>}
              </div>

              <div className="space-y-2">
                <label className="block text-[18px] font-medium text-slate-700">
                  <span className="text-rose-500">*</span> Password
                </label>
                <Input.Password
                  size="large"
                  prefix={<FiLock className="mr-2 text-sky-600" />}
                  name="password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                  status={formErrors.password ? 'error' : ''}
                  className={`h-14 rounded-[10px] text-[16px] ${!formErrors.password ? 'border-slate-200' : ''}`}
                />
                {formErrors.password && <div className="text-[#ff4d4f] text-[14px] mt-1">{formErrors.password}</div>}
              </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="w-full h-14 rounded-[10px] border-0 bg-[linear-gradient(180deg,#0b5f95_0%,#0a4c7a_100%)] text-[16px] font-semibold shadow-none hover:!bg-[linear-gradient(180deg,#0a5384_0%,#083c61_100%)]"
              >
                SIGN IN
              </Button>

              <div className="relative flex items-center py-1">
                <div className="flex-grow border-t border-slate-200" />
                <span className="mx-3 text-[13px] uppercase tracking-wide text-slate-400">or</span>
                <div className="flex-grow border-t border-slate-200" />
              </div>

              <Button
                type="default"
                onClick={handleHrmsSso}
                className="w-full h-14 rounded-[10px] border border-sky-600 text-[16px] font-semibold text-sky-700 hover:!text-sky-800 hover:!border-sky-700"
              >
                Sign in with HRMS
              </Button>

              <div className="pt-4 text-center text-[17px] text-slate-600">
                Forgot your login details?{' '}
                <Link to="/forgot-password" className="font-medium text-sky-500 underline decoration-sky-400/40 underline-offset-4">
                  Get help logging in.
                </Link>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
